/**
 * Technical Audit Page
 *
 * Hub view: one site card per audited domain, with embedded severity stats
 * and an expandable audit history. Click a card to view that run's issues.
 */

import { Alert, Button, Spin, Tooltip } from "antd";
import { Plus, RefreshCw, FileSearch } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { useAuditRuns } from "@/hooks/useTechnicalAuditIssues";
import { useTenantStore } from "@/stores/useTenantStore";
import { AuditSiteCard } from "@/components/TechnicalAudit/AuditSiteCard";
import { StartAuditModal } from "@/components/TechnicalAudit/StartAuditModal";
import type { AuditRunSummary } from "@/types/technicalAudit";

export default function TechnicalAuditPage() {
	const navigate = useNavigate();
	const { currentTenant: tenantId } = useTenantStore();
	const [page, setPage] = useState(1);
	const [showStartModal, setShowStartModal] = useState(false);

	// No fallback to the AgileCo test tenant: with no client selected the
	// runs query stays disabled (useAuditRuns gates on !!tenantId) and the
	// page shows its own empty state instead of another tenant's data.
	const { data, isLoading, error, refetch } = useAuditRuns(tenantId, page);

	const primaryRuns = data?.runs || [];
	const displayedRuns = primaryRuns;
	const displayedTenantId = tenantId;

	const runsByWebsite = useMemo(() => {
		// Group by a NORMALIZED website so protocol / leading www. / trailing
		// slash variants of the same domain collapse into ONE site card. Grouping
		// by the raw URL split "https://www.example.com/" and
		// "https://example.com/" into two cards for the same tenant.
		const normalizeKey = (url: string) =>
			url
				.trim()
				.toLowerCase()
				.replace(/^https?:\/\//, "")
				.replace(/^www\./, "")
				.replace(/\/+$/, "");
		const groups = new Map<string, { website: string; runs: AuditRunSummary[] }>();
		for (const run of displayedRuns) {
			const key = normalizeKey(run.website || "") || displayedTenantId || "unknown";
			const g = groups.get(key) ?? { website: run.website || key, runs: [] };
			g.runs.push(run);
			groups.set(key, g);
		}
		// Display the newest run's website as the card title (the canonical URL
		// the user actually crawls), while all runs share the one history.
		return Array.from(groups.values()).map(({ website, runs }): [string, AuditRunSummary[]] => {
			const newest = [...runs].sort(
				(a, b) =>
					new Date(b.started_at || 0).getTime() - new Date(a.started_at || 0).getTime(),
			)[0];
			return [newest?.website || website, runs];
		});
	}, [displayedRuns, displayedTenantId]);

	const handleAuditStarted = (runId: string) => {
		setShowStartModal(false);
		navigate(`/audit/technical-issues?run_id=${runId}`);
		refetch();
	};

	const handleSelectRun = (runId: string) => {
		navigate(`/audit/technical-issues?run_id=${runId}`);
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-96">
				<Spin size="large" />
			</div>
		);
	}

	if (error) {
		return (
			<div className="p-6">
				<Alert
					type="error"
					message="Failed to load audit runs"
					description={error instanceof Error ? error.message : "Unknown error"}
					action={
						<Button
							onClick={() => {
								refetch();
							}}
						>
							Retry
						</Button>
					}
				/>
			</div>
		);
	}

	const hasRuns = runsByWebsite.length > 0;

	return (
		<div className="w-full">
			<div className="flex items-start justify-between mb-2">
				<div>
					<h1 className="text-2xl font-semibold text-foreground">Technical Audits</h1>
					<p className="text-sm text-muted-foreground mt-1">
						Crawl-based site health reports with severity breakdown and fix tracking.
					</p>
					<p className="text-xs text-muted-foreground mt-2">
						Showing audits for{" "}
						<span className="font-medium text-foreground">{displayedTenantId}</span>
					</p>
				</div>
				<div className="flex gap-2">
					<Tooltip title="Single-page audit — coming soon">
						<Button
							icon={<FileSearch className="w-4 h-4" />}
							disabled
						>
							Audit Single Page
						</Button>
					</Tooltip>
					<Button
						icon={<RefreshCw className="w-4 h-4" />}
						onClick={() => refetch()}
					>
						Refresh
					</Button>
					<Button
						type="primary"
						icon={<Plus className="w-4 h-4" />}
						onClick={() => setShowStartModal(true)}
					>
						Start New Audit
					</Button>
				</div>
			</div>

			<div className="mt-6">
				{hasRuns ? (
					runsByWebsite.map(([website, runs]) => (
						<AuditSiteCard
							key={website}
							website={website}
							runs={runs}
							onSelectRun={handleSelectRun}
						/>
					))
				) : (
					<div className="rounded-xl border bg-card px-8 py-16 text-center">
						<FileSearch className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
						<h3 className="text-lg font-semibold text-foreground">No audits yet</h3>
						<p className="text-sm text-muted-foreground mt-1 mb-5">
							No technical audit runs were found for {displayedTenantId}.
						</p>
						<div className="flex justify-center gap-2">
							<Button
								type="primary"
								icon={<Plus className="w-4 h-4" />}
								onClick={() => setShowStartModal(true)}
							>
								Start New Audit
							</Button>
						</div>
					</div>
				)}
			</div>

			{showStartModal && (
				<StartAuditModal
					onClose={() => setShowStartModal(false)}
					onStarted={handleAuditStarted}
				/>
			)}
		</div>
	);
}
